import { create } from "zustand";

/**
 * Swapping the live theme stylesheet after a rebrand.
 *
 * Every colour and typeface reaches the page through `/api/theme.css`, a
 * render-blocking `<link>` in `index.html`. Saving the branding page used to
 * refresh the theme *document* — names, logos, the chart palette — and leave
 * that stylesheet alone, so the name changed on screen and every colour and
 * font stayed exactly as it was until a full reload. That is the "I changed
 * the theme and nothing happened" report.
 *
 * The new sheet is loaded beside the old one and the old one is removed only
 * once the new one has applied, so the swap never flashes an unstyled page.
 * The favicon is re-pointed the same way.
 */

interface StylesheetState {
  /** Bumped when a new stylesheet has *applied* — after `load`, not before. */
  revision: number;
  bump: () => void;
}

export const useThemeStylesheet = create<StylesheetState>((set) => ({
  revision: 0,
  bump: () => set((state) => ({ revision: state.revision + 1 })),
}));

const SHEET = 'link[rel="stylesheet"][href^="/api/theme.css"]';
const ICON = 'link[rel="icon"]';

export function reloadThemeStylesheet(): Promise<void> {
  if (typeof document === "undefined") return Promise.resolve();
  const stamp = Date.now().toString(36);

  const icon = document.querySelector<HTMLLinkElement>(ICON);
  if (icon) icon.href = `/favicon.svg?v=${stamp}`;

  const current = document.querySelectorAll<HTMLLinkElement>(SHEET);
  const next = document.createElement("link");
  next.rel = "stylesheet";
  next.href = `/api/theme.css?v=${stamp}`;

  return new Promise((resolve) => {
    let settled = false;
    const settle = (applied: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      if (applied) {
        current.forEach((link) => link.remove());
        useThemeStylesheet.getState().bump();
      } else {
        next.remove();
      }
      resolve();
    };
    // Two saves in quick succession: the second sheet can load first and
    // remove this one before its `load` fires, which then never does. The
    // timer makes sure nothing waiting on this promise waits forever.
    const timer = window.setTimeout(() => settle(next.isConnected), 10_000);
    next.addEventListener("load", () => settle(true), { once: true });
    next.addEventListener("error", () => settle(false), { once: true });
    const last = current[current.length - 1];
    if (last) last.after(next);
    else document.head.append(next);
  });
}
